// MERIDIAN — Three.js scene (custom mechanical keyboard, exploded → reassembled)
// Procedural dummy keyboard composed of named layer groups so a GLB can be hot-swapped later.

import * as THREE from 'https://unpkg.com/three@0.160.0/build/three.module.js';
import { GLTFLoader } from 'https://unpkg.com/three@0.160.0/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'https://unpkg.com/three@0.160.0/examples/jsm/environments/RoomEnvironment.js';

// Per-layer GLB sources. Set a URL to swap that layer's procedural geometry
// for a real GLB. The GLB is centered + scaled to a target footprint, then
// dropped into the existing named layer Group, so chapter choreography
// (layerOff / vis / explode) keeps working unchanged.
const LAYER_MODELS = {
  caps: window.MERIDIAN_ASSETS?.caps || '',
  switches: window.MERIDIAN_ASSETS?.switches || '',
  pcba: window.MERIDIAN_ASSETS?.pcba || '',
  case_bottom: window.MERIDIAN_ASSETS?.case_bottom || '',
};
// expected mesh / group names in the GLB (we'll match these for layer animation + hover)
const LAYERS = ['caps', 'switches', 'plate', 'pcba', 'case_top', 'case_bottom'];
const HOVER_PARTS = ['caps', 'switches', 'plate', 'pcba', 'mcu', 'stab', 'case_top', 'keyboard'];

// ── core ───────────────────────────────────────────────────────────────────
const canvas = document.getElementById('three-canvas');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.setClearColor(0x000000, 0);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.outputColorSpace = THREE.SRGBColorSpace;

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
camera.position.set(0, 1.5, 9);

// Environment map removed — GLBs render with only direct lights, no reflections.
// const pmrem = new THREE.PMREMGenerator(renderer);
// scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
keyLight.position.set(3, 5, 4);
scene.add(keyLight);
const fillLight = new THREE.DirectionalLight(0xc8d6ff, 0.6);
fillLight.position.set(-4, 1, 2);
scene.add(fillLight);
const rimLight = new THREE.DirectionalLight(0xfff1e0, 1.4);
rimLight.position.set(-1, -3, -3);
scene.add(rimLight);
const ambient = new THREE.AmbientLight(0xffffff, 0.35);
scene.add(ambient);

// post-processing removed — render directly for a flat, no-bloom look.

// ── procedural dummy keyboard ──────────────────────────────────────────────
const keyboard = new THREE.Group();
keyboard.name = 'keyboard';

// 65% layout — 15 cols × 5 rows
const COLS = 15;
const ROWS = 5;
const KEY_SIZE = 0.36;
const KEY_GAP = 0.04;
const PITCH = KEY_SIZE + KEY_GAP;
const KB_W = COLS * PITCH - KEY_GAP;
const KB_D = ROWS * PITCH - KEY_GAP;

const palettes = {
  graphite: { case: 0x2a2c30, caps: 0xe8e6df, accentCap: 0xc1452f, plate: 0xb6b8bd, pcb: 0x123a2e, mcu: 0x111418 },
  ivory:    { case: 0xece8de, caps: 0xf6f3ec, accentCap: 0x445566, plate: 0xb6b8bd, pcb: 0x123a2e, mcu: 0x111418 },
  smoke:    { case: 0xb8c0c8, caps: 0x2a2d33, accentCap: 0xd9b46a, plate: 0xb6b8bd, pcb: 0x123a2e, mcu: 0x111418 },
  walnut:   { case: 0x6e4a2e, caps: 0xe8e2d2, accentCap: 0x8b6f47, plate: 0xb6b8bd, pcb: 0x123a2e, mcu: 0x111418 },
};

let currentPalette = 'graphite';

function makeMaterials(p) {
  return {
    case: new THREE.MeshPhysicalMaterial({ color: p.case, metalness: 0.4, roughness: 0.55, clearcoat: 0.3, clearcoatRoughness: 0.4 }),
    caps: new THREE.MeshPhysicalMaterial({ color: p.caps, metalness: 0.0, roughness: 0.6 }),
    accent: new THREE.MeshPhysicalMaterial({ color: p.accentCap, metalness: 0.0, roughness: 0.55 }),
    plate: new THREE.MeshPhysicalMaterial({ color: p.plate, metalness: 1.0, roughness: 0.35 }),
    pcb: new THREE.MeshStandardMaterial({ color: p.pcb, metalness: 0.1, roughness: 0.6 }),
    mcu: new THREE.MeshStandardMaterial({ color: p.mcu, metalness: 0.4, roughness: 0.4 }),
    switchHousing: new THREE.MeshPhysicalMaterial({ color: 0x1a1a1a, metalness: 0.0, roughness: 0.7 }),
    switchTop: new THREE.MeshPhysicalMaterial({ color: 0xe6e1d4, metalness: 0.0, roughness: 0.5, transmission: 0.15 }),
    switchStem: new THREE.MeshStandardMaterial({ color: 0xd14a3a, metalness: 0.0, roughness: 0.5 }),
    pin: new THREE.MeshStandardMaterial({ color: 0xc89b56, metalness: 1.0, roughness: 0.3 }),
  };
}

let mats = makeMaterials(palettes[currentPalette]);

// ─── layer: case_bottom ───
const caseBottom = new THREE.Group(); caseBottom.name = 'case_bottom';
const cbGeo = new THREE.BoxGeometry(KB_W + 0.6, 0.32, KB_D + 0.6);
const cbMesh = new THREE.Mesh(cbGeo, mats.case);
cbMesh.position.y = -0.16;
caseBottom.add(cbMesh);
// feet
[[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) => {
  const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.06, 16), mats.case);
  foot.position.set(sx * (KB_W / 2 - 0.3), -0.34, sz * (KB_D / 2 - 0.3));
  caseBottom.add(foot);
});

// ─── layer: pcba ───
const pcba = new THREE.Group(); pcba.name = 'pcba';
const pcbGeo = new THREE.BoxGeometry(KB_W + 0.2, 0.04, KB_D + 0.2);
const pcbMesh = new THREE.Mesh(pcbGeo, mats.pcb);
pcba.add(pcbMesh);
// MCU chip
const mcuMesh = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.06, 0.3), mats.mcu);
mcuMesh.name = 'mcu';
mcuMesh.position.set(KB_W / 2 - 0.5, 0.04, -KB_D / 2 + 0.3);
pcba.add(mcuMesh);
// USB-C port
const usb = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.1, 0.16), mats.plate);
usb.position.set(0, 0.04, -KB_D / 2 - 0.04);
pcba.add(usb);
// gold pads in a grid (visual interest)
const padGeo = new THREE.CircleGeometry(0.04, 12);
for (let r = 0; r < ROWS; r++) {
  for (let c = 0; c < COLS; c++) {
    const pad = new THREE.Mesh(padGeo, mats.pin);
    pad.rotation.x = -Math.PI / 2;
    pad.position.set(-KB_W / 2 + KEY_SIZE / 2 + c * PITCH, 0.025, -KB_D / 2 + KEY_SIZE / 2 + r * PITCH);
    pcba.add(pad);
  }
}

// ─── layer: plate (with stabilizers) ───
const plate = new THREE.Group(); plate.name = 'plate';
const plateBase = new THREE.Mesh(new THREE.BoxGeometry(KB_W + 0.1, 0.04, KB_D + 0.1), mats.plate);
plate.add(plateBase);
// switch cutouts visual: small dark rectangles
const cutoutGeo = new THREE.BoxGeometry(0.28, 0.045, 0.28);
const cutoutMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, metalness: 0.0, roughness: 0.9 });
for (let r = 0; r < ROWS; r++) {
  for (let c = 0; c < COLS; c++) {
    const co = new THREE.Mesh(cutoutGeo, cutoutMat);
    co.position.set(-KB_W / 2 + KEY_SIZE / 2 + c * PITCH, 0.0, -KB_D / 2 + KEY_SIZE / 2 + r * PITCH);
    plate.add(co);
  }
}
// stabilizer bars (bottom row long keys: space + shifts)
const stab = new THREE.Group(); stab.name = 'stab';
const stabBar = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.05, 0.08), mats.pin);
stabBar.position.set(0.4, -0.04, KB_D / 2 - PITCH / 2 - 0.0);
stab.add(stabBar);
plate.add(stab);

// ─── layer: switches ───
const switches = new THREE.Group(); switches.name = 'switches';
const swHousingGeo = new THREE.BoxGeometry(0.27, 0.18, 0.27);
const swTopGeo = new THREE.BoxGeometry(0.21, 0.12, 0.21);
const swStemGeo = new THREE.BoxGeometry(0.06, 0.08, 0.06);
for (let r = 0; r < ROWS; r++) {
  for (let c = 0; c < COLS; c++) {
    const sw = new THREE.Group();
    const housing = new THREE.Mesh(swHousingGeo, mats.switchHousing);
    housing.position.y = 0.09;
    sw.add(housing);
    const top = new THREE.Mesh(swTopGeo, mats.switchTop);
    top.position.y = 0.18;
    sw.add(top);
    const stem = new THREE.Mesh(swStemGeo, mats.switchStem);
    stem.position.y = 0.26;
    sw.add(stem);
    sw.position.set(-KB_W / 2 + KEY_SIZE / 2 + c * PITCH, 0, -KB_D / 2 + KEY_SIZE / 2 + r * PITCH);
    switches.add(sw);
  }
}

// ─── layer: caps ───
const caps = new THREE.Group(); caps.name = 'caps';
const capGeo = new THREE.BoxGeometry(KEY_SIZE - 0.02, 0.16, KEY_SIZE - 0.02);
// give caps a subtle scoop by scaling top — use a second slightly inset box
const capScoopGeo = new THREE.BoxGeometry(KEY_SIZE - 0.08, 0.02, KEY_SIZE - 0.08);
const accentCols = new Set([0, COLS - 1]); // edge columns get accent
for (let r = 0; r < ROWS; r++) {
  for (let c = 0; c < COLS; c++) {
    const isAccent = (r === 0 && c === COLS - 1) || (r === ROWS - 1 && c === 0); // small accent placement
    const m = isAccent ? mats.accent : mats.caps;
    const cap = new THREE.Mesh(capGeo, m);
    const scoop = new THREE.Mesh(capScoopGeo, m);
    scoop.position.y = 0.08;
    const g = new THREE.Group();
    g.add(cap); g.add(scoop);
    g.position.set(-KB_W / 2 + KEY_SIZE / 2 + c * PITCH, 0.08, -KB_D / 2 + KEY_SIZE / 2 + r * PITCH);
    caps.add(g);
  }
}

// ─── stack assembly ───
// Y baselines when fully assembled
const BASE_Y = {
  case_bottom: 0,
  pcba: 0.18,
  plate: 0.30,
  switches: 0.36,
  case_top: 0.30,
  caps: 0.62,
};

// case_top — frame ring around the plate
const caseTop = new THREE.Group(); caseTop.name = 'case_top';
const ringT = 0.28;
const ringH = 0.16;
const top1 = new THREE.Mesh(new THREE.BoxGeometry(KB_W + 0.6, ringH, ringT), mats.case);
top1.position.set(0, 0, KB_D / 2 + ringT / 2 + 0.05);
const top2 = top1.clone(); top2.position.z = -(KB_D / 2 + ringT / 2 + 0.05);
const top3 = new THREE.Mesh(new THREE.BoxGeometry(ringT, ringH, KB_D + 0.6 + ringT * 2 + 0.1), mats.case);
top3.position.set(KB_W / 2 + ringT / 2 + 0.05, 0, 0);
const top4 = top3.clone(); top4.position.x = -(KB_W / 2 + ringT / 2 + 0.05);
caseTop.add(top1); caseTop.add(top2); caseTop.add(top3); caseTop.add(top4);

// place all layers at base Y
caseBottom.position.y = BASE_Y.case_bottom;
pcba.position.y = BASE_Y.pcba;
plate.position.y = BASE_Y.plate;
switches.position.y = BASE_Y.switches;
caseTop.position.y = BASE_Y.case_top;
caps.position.y = BASE_Y.caps;

keyboard.add(caseBottom);
keyboard.add(pcba);
keyboard.add(plate);
keyboard.add(switches);
keyboard.add(caseTop);
keyboard.add(caps);

// Strip ALL procedural placeholder geometry at startup. Each layer Group is
// emptied so nothing is rendered until its GLB loads. Avoids the placeholder
// geometry briefly flashing before the real model takes over.
['caps', 'switches', 'plate', 'pcba', 'case_top', 'case_bottom'].forEach(k => {
  const g = { caps, switches, plate, pcba, case_top: caseTop, case_bottom: caseBottom }[k];
  while (g.children.length) g.remove(g.children[0]);
});

scene.add(keyboard);
keyboard.position.y = -0.4;

// ── GLB hot-swap (per-layer) ───────────────────────────────────────────────
// Map layer key → its container Group so we can swap children in place.
const LAYER_GROUPS = {
  caps, switches, plate, pcba, case_top: caseTop, case_bottom: caseBottom,
};
window.LAYER_GROUPS = LAYER_GROUPS;
window.THREE = THREE;
// Target footprint for each loaded GLB (in scene units). We fit the model's
// XZ extents to this so different scans share a consistent visual scale.
const LAYER_FIT = {
  caps:        { width: KB_W,        depth: KB_D       },
  switches:    { width: KB_W,        depth: KB_D       },
  plate:       { width: KB_W + 0.1,  depth: KB_D + 0.1 },
  pcba:        { width: KB_W + 0.2,  depth: KB_D + 0.2 },
  case_top:    { width: KB_W + 0.6,  depth: KB_D + 0.6 },
  case_bottom: { width: KB_W + 0.6,  depth: KB_D + 0.6 },
};

const gltfLoader = new GLTFLoader();
const loadedLayers = new Set();

// Once the case GLB loads we lock in a single scale factor used by every other
// GLB. This keeps caps/switches/pcba in real-world proportion to the case
// instead of each being independently rescaled to fill the frame.
let SHARED_SCALE = null;
let caseFootprint = null; // { width, depth, height } in scene units (post-scale)

// Per-layer Y placement when SHARED_SCALE is active. Heights are measured from
// the case's interior floor upward and tuned so a 60% stack reads correctly.
// case_bottom rests at y=0 (sets the floor). pcba sits on case_bottom's interior.
// switches plug into pcba. caps cap the switches.
const SHARED_BASE_Y = {
  case_bottom: 0,
  pcba:        0,
  plate:       0,
  switches:    0,
  case_top:    0,
  caps:        0,
};

async function loadLayerGLB(layerKey, url) {
  if (!url) return;
  const group = LAYER_GROUPS[layerKey];
  if (!group) { console.warn('Unknown layer:', layerKey); return; }
  try {
    const gltf = await new Promise((res, rej) => gltfLoader.load(url, res, undefined, rej));
    const root = gltf.scene;

    const box = new THREE.Box3().setFromObject(root);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // Determine scale.
    // case_bottom defines the shared scale; everyone else uses that same factor.
    // Fallback: if case loads after another layer (unlikely), each layer fits
    // its own footprint independently (old behavior).
    let s;
    if (layerKey === 'case_bottom') {
      const fit = LAYER_FIT.case_bottom;
      const sx = fit.width / Math.max(size.x, 1e-6);
      const sz = fit.depth / Math.max(size.z, 1e-6);
      s = Math.min(sx, sz);
      SHARED_SCALE = s;
      caseFootprint = { width: size.x * s, depth: size.z * s, height: size.y * s };
      // All layers share origin (0,0,0) — no Y offset stacking.
      BASE_Y.case_bottom = 0;
      BASE_Y.pcba        = 0;
      BASE_Y.plate       = 0;
      BASE_Y.switches    = 0;
      BASE_Y.case_top    = 0;
      BASE_Y.caps        = 0;
    } else if (SHARED_SCALE != null) {
      s = SHARED_SCALE;
    } else {
      // case hasn't loaded yet — keep old per-layer fit as fallback
      const fit = LAYER_FIT[layerKey] || { width: KB_W, depth: KB_D };
      const sx = fit.width / Math.max(size.x, 1e-6);
      const sz = fit.depth / Math.max(size.z, 1e-6);
      s = Math.min(sx, sz);
    }
    root.scale.setScalar(s);

    // Use each GLB's authored origin directly — do not recenter on its bounding box.
    root.position.x = 0;
    root.position.z = 0;
    root.position.y = 0;

    while (group.children.length) group.remove(group.children[0]);
    group.add(root);

    // Force opaque materials — some GLBs author keycaps/cases with low opacity
    // or transmission (PBT plastic, glass) that makes the layer see-through.
    // We want layers to be solid; transparency is reserved for chapter fades.
    // Use GLB materials as-authored — no overrides.

    // Re-apply the (possibly updated) BASE_Y to every layer so the stack
    // re-aligns whenever the case finishes loading.
    Object.entries(LAYER_GROUPS).forEach(([k, g]) => { g.position.y = BASE_Y[k] ?? 0; });

    loadedLayers.add(layerKey);
    isProcedural = false;
  } catch (e) {
    console.warn(`GLB load failed for ${layerKey} (${url}):`, e);
  }
}

let isProcedural = true;
// Load case first so SHARED_SCALE is set, then load everything else in parallel.
async function loadAllLayerGLBs(map) {
  if (map.case_bottom) await loadLayerGLB('case_bottom', map.case_bottom);
  await Promise.all(
    Object.entries(map)
      .filter(([k]) => k !== 'case_bottom')
      .map(([k, url]) => loadLayerGLB(k, url))
  );
}
loadAllLayerGLBs(LAYER_MODELS);

// ── chapter choreography ───────────────────────────────────────────────────
// progress 0..1 over 8 chapters
// 0: hero (assembled)
// 1: caps lift up (peel off caps)
// 2: switches lift (caps stays up; switches separate)
// 3: plate+stab lifts (we focus there)
// 4: pcba reveal
// 5: case detail (top frame highlighted)
// 6: reassemble (everything slides back to assembled)
// 7: hero final

// Fully-exploded reference offsets — keep tight enough that all 4 layers stay in-frame.
const FULL_EXPLODE = { case_bottom: 0, case_top: 0.6, pcba: 0.45, plate: 0.8, switches: 1.1, caps: 1.5 };
const ALL_VIS  = { caps:1, switches:1, plate:1, pcba:1, case_top:1, case_bottom:1 };
// Dimmed visibility — same parts shown but at 60% opacity. Used for the
// "decorative background keyboard" in chapter 7/8 where the model floats
// behind the foreground paper card.
const DIM_VIS  = { caps:0.6, switches:0.6, plate:0.6, pcba:0.6, case_top:0.6, case_bottom:0.6 };
const NONE_VIS = { caps:0, switches:0, plate:0, pcba:0, case_top:0, case_bottom:0 };
// Helpers: focused-only vis (only the named layer(s) visible)
const only = (...keys) => {
  const v = { ...NONE_VIS };
  keys.forEach(k => { v[k] = 1; });
  return v;
};
// Centered offset so a single focused layer sits near y=0 in the camera frame
// (subtract the layer's BASE_Y to bring it to origin)
const BASE_Y_LOCAL = { caps:0.62, switches:0.36, plate:0.30, pcba:0.18, case_top:0.30, case_bottom:0 };
const centerLayer = (k) => ({ ...NONE_VIS, [k]: -BASE_Y_LOCAL[k] + 0.6 }); // offset so layer sits ~y=0.6

// Detail-chapter camera presets. Each chapter has a `side` (which half of the
// frame the model lives in) and a layer Y; the preset supplies camera height/
// distance and keyboard rotation so we can swap aesthetic angles via Tweaks.
//
// dx is the horizontal offset from layer center; sign is flipped for side='left'.
// kbRotX/Y are absolute keyboard rotation; Y sign also flipped on left side so
// the visible 3/4 face always points toward the open half of the frame.
const ANGLE_PRESETS = {
  front:    { dx: 2.2, dy: 0.4,  dz: 6.5,  kbRotX: -0.10, kbRotY: 0.06 },  // straight-on, slight tilt
  threeq:   { dx: 1.7, dy: 1.4,  dz: 5.5,  kbRotX:  0.22, kbRotY: 1.05 },  // 3/4 hero (~60°, tilted forward)
  low:      { dx: 2.2, dy: -0.4, dz: 6.0,  kbRotX:  0.18, kbRotY: 0.22 },  // looking up
  top:      { dx: 0.8, dy: 4.2,  dz: 4.6,  kbRotX: -0.95, kbRotY: 0.04 },  // overhead
  side:     { dx: 0.0, dy: 0.4,  dz: 5.6,  kbRotX: -0.04, kbRotY: 1.55 },  // pure profile
  vertical: { dx: 0.2, dy: -1.4,  dz: 9.0,  kbRotX:  1.35, kbRotY: 0.20 },  // standing up, ~face-on (PCBA)
  vertical_caps: { dx: 0.0, dy: 0.0, dz: 5.5, kbRotX:  1.40, kbRotY: 0.18 },  // standing up tall, close — caps fill frame vertically
  macro:    { dx: 0.6, dy: 0.4,  dz: 1.8,  kbRotX: -0.35, kbRotY: 0.55 },  // tight close-up
  hero:     { dx: 1.6, dy: 2.4,  dz: 5.8,  kbRotX: -0.55, kbRotY: 0.85 },  // elevated 3/4, looking down on tops
};

// Apply a preset to a detail chapter, given the side and the y-position of the focused layer.
function camForPreset(presetName, side /* 'left' | 'right' */, layerY) {
  const p = ANGLE_PRESETS[presetName] || ANGLE_PRESETS.threeq;
  const sx = side === 'left' ? -1 : 1;
  return {
    camPos:  [sx * p.dx, p.dy + layerY, p.dz],
    camLook: [sx * (p.dx - 1.4), layerY, 0],
    kbRot:   [p.kbRotX, sx * p.kbRotY, 0],
  };
}

const detailChapterAngles = { case: 'threeq', pcba: 'vertical', switches: 'threeq', caps: 'vertical_caps' };
function rebuildChapters() {
  const caseCfg     = camForPreset(detailChapterAngles.case,     'left',  0.4);
  const pcbaCfg     = camForPreset(detailChapterAngles.pcba,     'right', 0.6);
  const switchesCfg = camForPreset(detailChapterAngles.switches, 'left',  0.8);
  const capsCfg     = camForPreset(detailChapterAngles.caps,     'right', 0.9);
  chapters[3] = { ...caseCfg,     layerOff: { ...NONE_VIS, case_bottom: 0.5, case_top: 1.1 }, vis: { ...NONE_VIS, case_top:1, case_bottom:1 } };
  chapters[4] = { ...pcbaCfg,     layerOff: { ...NONE_VIS, pcba: SOLO_OFFSETS.pcba },           vis: only('pcba') };
  chapters[5] = { ...switchesCfg, layerOff: { ...NONE_VIS, switches: SOLO_OFFSETS.switches },   vis: only('switches') };
  chapters[6] = { ...capsCfg,     layerOff: { ...NONE_VIS, caps: SOLO_OFFSETS.caps },           vis: only('caps') };
}
const SOLO_OFFSETS = {
// SOLO offsets — tuned so the layer's bbox-center lands near the chapter's
// camLook.y given each GLB's authored Y offset. Measured at runtime:
//   pcba builtin Y ≈ -0.01, camLook 0.6  → 0.61
//   switches builtin Y ≈ +0.55, camLook 0.8 → 0.25
//   caps builtin Y ≈ +0.28, camLook 0.9 → 0.62
  case_bottom: 0.6,
  case_top:    0.6 - 0.30 + 0.6,
  pcba:        0.61,
  plate:       0.9 - 0.30,
  switches:    0.25,
  caps:        0.62,
};

// Chapter order matches app.jsx CHAPTERS:
// 0 hero, 1 features, 2 structure, 3 case, 4 pcba, 5 switches, 6 caps,
// 7 voices, 8 custom, 9 faq, 10 buy
// ── chapter cameras: a single continuous camera path ────────────────
// Reworked so the camera flows along ONE smooth trajectory across the
// entire scroll, never zig-zagging. x drifts gently right → left across
// the detail chapters, then back to center; z dollies in for the parts
// showcase (3-5), pulls way out for voices/custom/faq, then settles for
// buy. kbRot.y monotonically passes from negative to positive without
// flipping direction. The result: a single sweeping move that reads as
// one shot, even when scrubbed in reverse.
const chapters = [
  // 0 hero — full assembled, side-on, model sits lower with text above
  { camPos: [0.0, 2.0, 11.0], camLook: [0.0, 1.2, 0], vis: { ...ALL_VIS }, layerOff: NONE_VIS, kbRot:[-0.18, -0.50, 0] },
  // 1 features — assembled, camera high so model sits in upper portion of frame
  { camPos: [0.4, 4.5, 12.0], camLook: [0.2, -0.8, 0], layerOff: NONE_VIS, vis: { ...ALL_VIS }, kbRot:[-0.18, -0.42, 0] },
  // 2 structure — exploded; camera pulls back and rises to frame all layers
  { camPos: [0.8, 3.0, 11.0], camLook: [0.3, 0.8, 0], layerOff: { ...FULL_EXPLODE }, vis: { ...ALL_VIS }, kbRot:[-0.22, -0.32, 0] },
  // 3 CASE — case parts only; cam continues drifting (slight right, deeper in)
  { camPos: [1.4, 1.6, 7.6], camLook: [0.6, 0.4, 0], layerOff: { ...NONE_VIS, case_bottom: 0.5, case_top: 1.1 }, vis: { ...NONE_VIS, case_top:1, case_bottom:1 }, kbRot:[-0.22, -0.10, 0] },
  // 4 PCBA — closer dolly, gentle continuation
  { camPos: [1.6, 1.4, 6.6], camLook: [0.7, 0.5, 0], layerOff: { ...NONE_VIS, pcba: SOLO_OFFSETS.pcba }, vis: only('pcba'), kbRot:[-0.26, 0.05, 0] },
  // 5 SWITCHES — closest dolly, cam continues toward right
  { camPos: [1.8, 1.6, 6.0], camLook: [0.8, 0.7, 0], layerOff: { ...NONE_VIS, switches: SOLO_OFFSETS.switches }, vis: only('switches'), kbRot:[-0.28, 0.18, 0] },
  // 6 KEYCAPS — caps only; cam apex of dolly-in, slightly higher
  { camPos: [2.0, 1.9, 6.2], camLook: [0.9, 0.9, 0], layerOff: { ...NONE_VIS, caps: SOLO_OFFSETS.caps }, vis: only('caps'), kbRot:[-0.30, 0.30, 0] },
  // 7 VOICES — pull out begins
  { camPos: [2.0, 2.0, 12.0], camLook: [0.9, 0.7, 0], layerOff: { ...FULL_EXPLODE }, vis: { ...DIM_VIS }, kbRot:[-0.28, 0.36, 0] },
  // 8 CUSTOM — pull out continues; cam drifts back toward center
  { camPos: [1.2, 2.0, 16.0], camLook: [0.5, 0.5, 0], layerOff: { ...FULL_EXPLODE }, vis: { ...DIM_VIS }, kbRot:[-0.24, 0.42, 0] },
  // 9 FAQ — apex of pull-out, fully centered
  { camPos: [0.0, 2.0, 18.0], camLook: [0.0, 0.4, 0], layerOff: { ...FULL_EXPLODE }, vis: { ...DIM_VIS }, kbRot:[-0.22, 0.50, 0] },
  // 10 BUY/AVAILABLE
  { camPos: [0.0, 2.4, 14.0], camLook: [0.0, -0.6, 0], layerOff: { ...NONE_VIS }, vis: { ...NONE_VIS }, kbRot:[-0.22, 0.50, 0] },
];

// ── runtime state ──────────────────────────────────────────────────────────
const state = {
  scrollProgress: 0,
  hoverPart: null,
  variant: 'bright',
  lightIntensity: 1,
  envIntensity: 1,
  bloom: 0.3,
  palette: 'graphite',
  explodeAmount: 1, // 0..1 multiplier for chapter offsets (Tweak)
};

const variants = {
  bright: { bg: '#FAFAFA', exposure: 1.05, key: 2.0, fill: 0.55, rim: 1.3, ambient: 0.4, bloom: 0.18 },
  marble: { bg: '#FAFAFA', exposure: 1.05, key: 2.0, fill: 0.55, rim: 1.3, ambient: 0.4, bloom: 0.18 },
  paper:  { bg: '#FAFAFA', exposure: 1.05, key: 2.0, fill: 0.55, rim: 1.3, ambient: 0.4, bloom: 0.18 },
  greige: { bg: '#F5F5F5', exposure: 1.0, key: 1.9, fill: 0.55, rim: 1.2, ambient: 0.45, bloom: 0.16 },
  dark:   { bg: '#0c0d10', exposure: 0.9, key: 2.8, fill: 0.25, rim: 2.2, ambient: 0.08, bloom: 0.55 },
  neutral:{ bg: '#FAFAFA', exposure: 1.0, key: 1.6, fill: 0.5, rim: 1.0, ambient: 0.5, bloom: 0.18 },
};

function applyVariant(name) {
  const v = variants[name] || variants.bright;
  state.variant = name;
  document.documentElement.style.background = v.bg;
  // Canvas stays transparent so the page marble shows through; v.bg is only the CSS fallback.
  scene.background = null;
  renderer.setClearColor(0xFAFAFA, 0);
  renderer.toneMappingExposure = v.exposure * state.lightIntensity;
  keyLight.intensity = v.key * state.lightIntensity;
  fillLight.intensity = v.fill * state.lightIntensity;
  rimLight.intensity = v.rim * state.lightIntensity;
  ambient.intensity = v.ambient * state.envIntensity;
  document.body.classList.toggle('is-dark', name === 'dark');
}

function applyPalette(name) {
  if (!palettes[name]) return;
  state.palette = name;
  currentPalette = name;
  const newMats = makeMaterials(palettes[name]);
  // recolor procedural materials in place
  Object.keys(mats).forEach(k => {
    if (newMats[k]) {
      mats[k].color.copy(newMats[k].color);
      if (mats[k].metalness !== undefined && newMats[k].metalness !== undefined) mats[k].metalness = newMats[k].metalness;
      if (mats[k].roughness !== undefined && newMats[k].roughness !== undefined) mats[k].roughness = newMats[k].roughness;
    }
  });
}

// ── interpolation ──────────────────────────────────────────────────────────
function lerp(a, b, t) { return a + (b - a) * t; }
function lerpV(a, b, t) { return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]; }
function easeInOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
function easeInCubic(t) { return t * t * t; }
// Reverse-aware easing: when the user is scrolling BACKWARD (down → up,
// from a later chapter to an earlier one), we want a softer settle. The
// forward direction uses easeInOutCubic which front- and back-loads
// motion; the reverse direction uses an ease-out-then-hold curve that
// holds the destination chapter near the end so the camera/rotation
// "lands" gently into the previous chapter instead of snapping. This is
// the heart of the smooth-rewind feel — every rewind gets a cushioned
// landing without the forward direction losing its punch.
//
// Curve shape (input tt = local segment progress 0..1, segDir = +1 forward, -1 reverse):
//   forward: easeInOutCubic(tt)                  → S-curve, symmetric
//   reverse: 1 - easeInCubic(1 - tt)             → ease-out, gentle landing
function easeReverseAware(tt, segDir) {
  if (segDir < 0) {
    // ease-out: slow start near b (the chapter we're leaving), accelerate
    // briefly, then decelerate hard into a (the chapter we're returning to).
    // Equivalent to easeOutCubic but emphasizing the late-stage settle.
    const u = 1 - tt;
    return 1 - (u * u * (3 - 2 * u)); // smoothstep-style, ease-out
  }
  return easeInOutCubic(tt);
}
// "Snap" easing: slow drift then a sharp click into place. Used for the
// REASSEMBLE → SPEC segment so layers feel pulled together magnetically.
// Critical: must REACH 1.0 well before t=1, because scroll-snap typically
// parks the page near tt≈0.93 (not 1.0). If layers aren't fully assembled
// by tt≈0.85, the SPEC chapter visibly shows the model still slightly
// disassembled. So we hit full-assemble (output=1.0) by t≈0.8.
function easeSnap(t) {
  if (t <= 0) return 0;
  if (t >= 0.8) return 1;
  // Ease-in quartic 0..0.55 across the first 50%
  if (t < 0.5) {
    const u = t / 0.5;
    return u * u * u * u * 0.55;
  }
  // Sharp ease-out 0.55 → 1.0 across t=0.5..0.8
  const u = (t - 0.5) / 0.3;
  return 0.55 + (1 - 0.55) * (1 - Math.pow(1 - u, 3));
}
function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

rebuildChapters();

const camCurrent = { pos: [...chapters[0].camPos], look: [...chapters[0].camLook] };
const targetLayerOff = { ...chapters[0].layerOff };
const targetVis = { ...chapters[0].vis };
const targetKbRot = [...chapters[0].kbRot];
// Extra per-layer transforms used ONLY by the BUY-assemble segment to make
// the reveal feel dynamic: each layer can have its own X/Z offset, an extra
// Y multiplier, and a spin (rotation about Y). All zeroed for normal segments.
// Per-layer "personality" — minimal lateral drift, NO spin. The motion
// reads as a clean, tall vertical lift rather than a chaotic explosion.
// Tiny x/z values just keep parts from stacking on a single axis so the
// silhouette stays readable; spin is 0 everywhere.
const BUY_DIR = {
  caps:        { x:  0.25, z:  0.10, spin: 0 },
  switches:    { x: -0.20, z:  0.15, spin: 0 },
  plate:       { x:  0.05, z:  0.25, spin: 0 },
  pcba:        { x: -0.05, z: -0.20, spin: 0 },
  case_top:    { x:  0.30, z: -0.10, spin: 0 },
  case_bottom: { x: -0.30, z: -0.15, spin: 0 },
};
// Stagger: each layer's animation is offset slightly so they don't all move
// in sync — caps lead, case parts trail. Values are in tt-space (0..1).
const BUY_STAGGER = {
  caps: 0.00, switches: 0.05, plate: 0.10,
  pcba: 0.15, case_top: 0.20, case_bottom: 0.25,
};
const targetLayerOffXZ = {}; // { [k]: {x, z} }
const targetLayerSpin = {};  // { [k]: radians }
for (const k of LAYERS) { targetLayerOffXZ[k] = { x: 0, z: 0 }; targetLayerSpin[k] = 0; }

// BUY → FAQ exit fade: when leaving AVAILABLE back to FAQ, instead of
// hard-snapping the keyboard off (which leaves a 1-frame ghost) or
// letting the default opacity lerp fade it slowly (which looks buggy
// because layer Y also resets and parts visibly slide), we run a
// dedicated 350ms fade-out from whatever assembled state the model was
// in. This ALWAYS plays cleanly and reads as an intentional transition.
const BUY_EXITFADE_MS = 350;
let buyExitFadeStart = -1;     // timestamp; -1 when not fading
let buyExitFadeFromOff = null; // { [k]: y } captured Y offsets at fade start
let buyExitFadeFromXZ = null;  // { [k]: {x,z} }
let buyExitFadeFromOpacity = null; // { [k]: number }
// kick off a 2.0s assemble animation that runs INDEPENDENTLY of scroll
// position. This lets parts start fully exploded and settle into the
// assembled pose over real time — not over scroll distance — so the user
// can land in segment 8, hold still, and watch it build itself.
const BUY_ASSEMBLE_MS = 2500;
let buyAssembleStart = -1;     // performance.now() when segment 8 was entered, -1 if not in segment
let buyWasInSegment = false;   // tracks edge so we only reset on enter

function ensureBuyTicker() {
  // No-op: animate() already runs every frame and re-evaluates BUY assemble
  // targets via tickBuyAssemble() below, so the timer-driven animation
  // progresses even when the user isn't scrolling.
}

// Per-frame BUY assemble update — called from animate() so the 2.5s
// settle progresses on real time, not on scroll events.
function tickBuyAssemble() {
  if (!buyWasInSegment || buyAssembleStart < 0) return;
  if (state.scrollProgress < 0.985) return;
  const elapsed = performance.now() - buyAssembleStart;
  const u0 = Math.max(0, Math.min(1, elapsed / BUY_ASSEMBLE_MS));
  const u = 0.5 - 0.5 * Math.cos(u0 * Math.PI);
  for (const k of LAYERS) {
    const explodeAmt = (FULL_EXPLODE[k] || 0) * 3.0;
    const dir = BUY_DIR[k] || { x: 0, z: 0, spin: 0 };
    const xzAmt = 1.0;
    targetLayerOff[k] = lerp(explodeAmt, 0, u);
    targetLayerOffXZ[k] = {
      x: lerp(dir.x * xzAmt, 0, u),
      z: lerp(dir.z * xzAmt, 0, u),
    };
    targetLayerSpin[k] = 0;
    targetVis[k] = 1;
  }
}

// ── reverse-direction detection ─────────────────────────────────────
// Direction is detected per-call from the sign of dp (current p − last p).
// No smoothing/hysteresis: any earlier latency made the first 2-3 frames
// of a rewind still apply forward easing, which read as "the camera
// jumps the wrong way" right at the moment of reversal. Instant flip
// is the right call here — the only risk (single-jitter-frame flicker)
// is mitigated by the 0.0005 dead-zone below.
let _lastScrollP = 0;
let _scrollDir = 1;             // +1 forward, -1 reverse, last detected
function updateScrollDir(p) {
  const dp = p - _lastScrollP;
  _lastScrollP = p;
  if (Math.abs(dp) > 0.0005) {
    _scrollDir = dp > 0 ? 1 : -1;
  }
  return _scrollDir;
}

function applyScroll(p) {
  const segDir = updateScrollDir(p);
  state.scrollProgress = p;
  const segs = chapters.length - 1;
  const f = Math.max(0, Math.min(0.9999, p)) * segs;
  const i = Math.floor(f);
  let tt = f - i;                         // raw 0..1 within the segment
  // For the assemble segment (KEYCAPS → REASSEMBLE, i===5), once the user is
  // ≥80% through, hard-lock to t=1 so the model lands fully assembled
  // and stays there — no residual drift. ONLY active on forward scroll;
  // on reverse the clamp would freeze the camera at chapter 5 for the
  // first 20% of the rewind, then snap, so we skip it.
  if (i === 6 && tt >= 0.8 && segDir > 0) tt = 1;
  // BUY segment (FAQ → BUY, i===8): a slow, cinematic reveal that uses
  // the FULL scroll segment. The user explicitly asked for more time —
  // so we DON'T compress tt here. The segment block below handles the
  // full 0..1 range (visibility kicks in early, parts drift apart, then
  // lazily magnet together as tt approaches 1).
  // CUSTOM segment (Voices → CUSTOM, i===6): both chapters hide all layers,
  // so no special-case needed — the default visibility lerp will keep the
  // model hidden through the whole transition. Removed the previous
  // explode-and-snap reveal because CUSTOM no longer shows the model.
  // (kept above clamp for KEYCAPS→VOICES still applies.)
  const t  = easeInOutCubic(tt);          // unified S-curve, forward & reverse
  // "Magnetic snap" curve for assemble transitions: KEYCAPS → REASSEMBLE
  // (i===5) and FAQ → BUY (i===8) both collapse layers into a clean
  // assembly. easeSnap front-loads motion so parts shoot in fast and
  // settle clean — feels like a magnetic click into place. On reverse
  // (rewinding away from the assembly), we use the symmetric S-curve
  // so the rewind matches what the user just saw, in reverse.
  const tLayer = ((i === 6 || i === 9) && segDir > 0) ? easeSnap(tt) : t;
  const a = chapters[i];
  const b = chapters[Math.min(i + 1, chapters.length - 1)];

  // ── camera + rotation: continuous easing, full segment ──
  camCurrent.pos  = lerpV(a.camPos,  b.camPos,  t);
  camCurrent.look = lerpV(a.camLook, b.camLook, t);
  targetKbRot[0]  = lerp(a.kbRot[0], b.kbRot[0], t);
  targetKbRot[1]  = lerp(a.kbRot[1], b.kbRot[1], t);
  targetKbRot[2]  = lerp(a.kbRot[2], b.kbRot[2], t);

  // ── visibility: BINARY hand-off at midpoint ──
  // No fractional values — every layer is either fully on or fully off at
  // any given scroll position. This kills "ghost layers" caused by
  // scroll-snap landing a hair off-center; layers can never be parked at
  // 50% opacity. The visual transition is carried by the spatial layerOff
  // animation (layers physically slide in/out), not by fading.
  const useB = tt >= 0.5;
  let visSrc = useB ? b : a;

  // ── (former CUSTOM assemble special-case removed) ──
  // CUSTOM no longer shows the model, so segment 6 (Voices → CUSTOM) just
  // falls through to the default lerp below — both chapters have all
  // layers hidden, so nothing animates and the section reads as empty.

  // ── BUY assemble — TIME-BASED, fires only when AVAILABLE is on screen ──
  // Chapter 9 has vis=NONE_VIS so the default chapter lerp keeps the
  // keyboard hidden across the entire FAQ → AVAILABLE scroll segment.
  // The keyboard ONLY appears when scrollProgress locks at ≥0.985 (the
  // AVAILABLE section is fully snapped on screen). At that point a 2.5s
  // timer starts and the keyboard assembles from the exploded pose. This
  // is the only place the AVAILABLE keyboard ever exists — outside this
  // block it stays invisible, no fade, no bleed, no ghost frames.
  const inAvailable = state.scrollProgress >= 0.985;
  if (inAvailable) {
    if (!buyWasInSegment) {
      buyAssembleStart = performance.now();
      buyWasInSegment = true;
    }
    const elapsed = performance.now() - buyAssembleStart;
    const u0 = Math.max(0, Math.min(1, elapsed / BUY_ASSEMBLE_MS));
    const u = 0.5 - 0.5 * Math.cos(u0 * Math.PI);

    visSrc = { vis: { ...ALL_VIS } };
    for (const k of LAYERS) {
      const explodeAmt = (FULL_EXPLODE[k] || 0) * 3.0;
      const dir = BUY_DIR[k] || { x: 0, z: 0, spin: 0 };
      const xzAmt = 1.0;
      targetLayerOff[k] = lerp(explodeAmt, 0, u);
      targetLayerOffXZ[k] = {
        x: lerp(dir.x * xzAmt, 0, u),
        z: lerp(dir.z * xzAmt, 0, u),
      };
      targetLayerSpin[k] = 0;
      targetVis[k] = 1;
    }
    if (u0 < 1) ensureBuyTicker();
    return;
  } else {
    // Left the AVAILABLE section — reset the timer so the next entry
    // re-runs the assemble from frame zero. Visibility falls back to the
    // chapter-default (NONE_VIS), so the keyboard disappears immediately.
    if (buyWasInSegment) {
      buyWasInSegment = false;
      buyAssembleStart = -1;
    }
  }
  for (const k of LAYERS) {
    targetLayerOffXZ[k] = { x: 0, z: 0 };
    targetLayerSpin[k] = 0;
  }

  for (const k of LAYERS) {
    // Spatial offset still eases continuously — lets a layer "fly out"
    // as the next chapter takes over, even though its opacity is binary.
    targetLayerOff[k] = lerp(a.layerOff[k] || 0, b.layerOff[k] || 0, tLayer);
    targetVis[k] = visSrc.vis?.[k] ?? 1;
  }
}

// Set opacity on every material in a group (preserving original opacity values)
function setGroupOpacity(group, opacity) {
  group.traverse(o => {
    if (!o.isMesh || !o.material) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    mats.forEach(m => {
      if (m.userData._origOpacity == null) m.userData._origOpacity = m.opacity ?? 1;
      m.transparent = opacity < 0.999;
      m.opacity = m.userData._origOpacity * opacity;
      m.depthWrite = opacity > 0.95;
    });
  });
  group.visible = opacity > 0.005;
}

// ── raycast (annotations) ──────────────────────────────────────────────────
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2(-10, -10);
window.addEventListener('pointermove', (e) => {
  const r = renderer.domElement.getBoundingClientRect();
  mouse.x = ((e.clientX - r.left) / r.width) * 2 - 1;
  mouse.y = -((e.clientY - r.top) / r.height) * 2 + 1;
});

function pickPart() {
  raycaster.setFromCamera(mouse, camera);
  const hits = raycaster.intersectObject(keyboard, true);
  if (!hits.length) return null;
  let o = hits[0].object;
  while (o && !HOVER_PARTS.includes(o.name) && o.parent) o = o.parent;
  return HOVER_PARTS.includes(o?.name) ? { name: o.name, point: hits[0].point.clone() } : null;
}

// ── resize ──
function resize() {
  const w = Math.max(1, window.innerWidth);
  const h = Math.max(1, window.innerHeight);
  renderer.setSize(w, h, false);
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  // Ensure CSS layout matches in case any parent stacking context interferes.
  const c = renderer.domElement;
  c.style.width = w + 'px';
  c.style.height = h + 'px';
  camera.aspect = w / h;
  // FOV: widening too far makes the model look SMALLER (perspective
  // compression), so keep portrait FOV moderate; rely on camera-Z pull
  // and lookAt-lift in the main loop to bring the model into frame.
  const minDim = Math.min(w, h);
  const portrait = h > w;
  let fov = 32;
  if (portrait) {
    if (minDim < 480) fov = 46;
    else if (minDim < 720) fov = 42;
    else if (minDim < 1024) fov = 38;
    else fov = 34;
  } else {
    if (minDim < 480) fov = 38;
    else if (minDim < 720) fov = 36;
  }
  camera.fov = fov;
  camera.updateProjectionMatrix();
  // Scale the keyboard group on narrow viewports so the model fills more
  // of the frame without affecting per-chapter camera framing.
  const _scale = w < 480 ? 1.5 : (w < 720 ? 1.35 : (w < 1024 ? 1.15 : 1));
  keyboard.scale.setScalar(_scale);
}
resize();
window.addEventListener('resize', resize);
window.addEventListener('orientationchange', () => setTimeout(resize, 80));
// iOS Safari hides/shows toolbar without firing resize reliably — poll briefly.
let _lastWH = window.innerWidth + 'x' + window.innerHeight;
setInterval(() => {
  const cur = window.innerWidth + 'x' + window.innerHeight;
  if (cur !== _lastWH) { _lastWH = cur; resize(); }
}, 500);

// ── main loop ──────────────────────────────────────────────────────────────
const clock = new THREE.Clock();
const tmpV = new THREE.Vector3();

function animate() {
  requestAnimationFrame(animate);
  const dt = clock.getDelta();
  const t = clock.elapsedTime;

  // Re-evaluate the time-based BUY assemble targets every frame so the
  // 2.0s settle plays on real time even if the user has stopped scrolling.
  tickBuyAssemble();

  // Camera follow: 0.85 — near-instant snap. Per user request,
  // chapter transitions for 0–9 should be effectively immediate; only
  // the AVAILABLE assemble (chapter 9) keeps a real timed animation.
  // On narrow/portrait viewports, scale Z out so the keyboard doesn't
  // crop, and pull X toward center so side-framed chapters still fit.
  // ── Mobile/portrait camera reframe ──────────────────────────────────
  // Desktop landscape (≥1024) uses the chapter's camPos/camLook as-is.
  // Narrow viewports need three things:
  //   1. Center X — side-framed chapters push the model off-screen on
  //      narrow widths, so we always center.
  //   2. Pull Z closer — portrait phones see less horizontal world, so we
  //      reduce camera distance to keep the model big.
  //   3. Shift lookAt UP — most chapter titles are vertically centered.
  //      Raising lookAt drops the model into the lower third of the frame
  //      where it doesn't fight the title.
  const _w = window.innerWidth, _h = window.innerHeight;
  const _portrait = _h > _w;
  let cx = camCurrent.pos[0], cy = camCurrent.pos[1], cz = camCurrent.pos[2];
  let lx = camCurrent.look[0], ly = camCurrent.look[1], lz = camCurrent.look[2];
  if (_w < 1024) {
    // recenter X on side-framed chapters
    const xFactor = _w < 720 ? 0 : 0.35;
    cx *= xFactor; lx *= xFactor;
    // pull Z out a bit so the model fits within the frame without cropping
    const zFactor = _w < 480 ? 1.25 : (_w < 720 ? 1.15 : 1.0);
    cz *= zFactor;
    // portrait: small downward shift to center the model vertically
    if (_portrait) {
      const liftBase = _w < 480 ? -0.3 : (_w < 720 ? -0.2 : -0.1);
      ly += liftBase;
    }
  }
  camera.position.lerp(tmpV.set(cx, cy, cz), 0.85);
  camera.lookAt(tmpV.set(lx, ly, lz));

  // keyboard idle wobble + chapter rot
  const wobbleY = Math.sin(t * 0.4) * 0.04;
  keyboard.rotation.x += (targetKbRot[0] - keyboard.rotation.x) * 0.08;
  keyboard.rotation.y += (targetKbRot[1] + wobbleY - keyboard.rotation.y) * 0.08;
  keyboard.rotation.z += (targetKbRot[2] - keyboard.rotation.z) * 0.08;

  // apply layer offsets (Y position above base) with smoothing + visibility fade
  const layerObjs = { caps, switches, plate, pcba, case_top: caseTop, case_bottom: caseBottom };
  for (const k of LAYERS) {
    const target = BASE_Y[k] + (targetLayerOff[k] || 0) * state.explodeAmount;
    const obj = layerObjs[k];
    if (!obj) continue;
    // Per-layer follow: near-instant snap (0.85) so the model tracks
    // scroll position with no perceptible delay across chapters 0–9.
    // The AVAILABLE assemble (chapter 9) drives its own values via
    // tickBuyAssemble() which IS still a timed animation — the high
    // lerp factor here just snaps to whatever target it sets each frame.
    obj.position.y += (target - obj.position.y) * 0.85;
    const xz = targetLayerOffXZ[k] || { x: 0, z: 0 };
    obj.position.x += (xz.x - obj.position.x) * 0.85;
    obj.position.z += (xz.z - obj.position.z) * 0.85;
    const spinTgt = targetLayerSpin[k] || 0;
    obj.rotation.y += (spinTgt - obj.rotation.y) * 0.85;
    // smooth opacity — only re-apply to materials when it actually changes
    const tv = targetVis[k] ?? 1;
    const prev = obj.userData._opacity ?? tv;
    const next = prev + (tv - prev) * 0.85;
    obj.userData._opacity = next;
    if (Math.abs(next - (obj.userData._appliedOpacity ?? -1)) > 0.005) {
      setGroupOpacity(obj, next);
      obj.userData._appliedOpacity = next;
    }
    obj.visible = next > 0.02;
  }

  // raycast hover
  const hit = pickPart();
  const newPart = hit?.name || null;
  if (newPart !== state.hoverPart) {
    state.hoverPart = newPart;
    window.dispatchEvent(new CustomEvent('kb-hover', { detail: { part: newPart, point: hit?.point } }));
  }
  if (hit) {
    const v = hit.point.clone().project(camera);
    const x = (v.x * 0.5 + 0.5) * window.innerWidth;
    const y = (-v.y * 0.5 + 0.5) * window.innerHeight;
    window.dispatchEvent(new CustomEvent('kb-hover-pos', { detail: { x, y } }));
  }

  // Per-layer screen positions — only computed when the overview chapter is in view.
  // setFromObject is expensive (traverses the whole group), so gate it.
  if (state.scrollProgress >= 0.05 && state.scrollProgress <= 0.30) {
    const layerScreen = {};
    const _bb = new THREE.Box3();
    const _wp = new THREE.Vector3();
    for (const k of LAYERS) {
      const obj = layerObjs[k];
      if (!obj || !obj.visible) continue;
      _bb.setFromObject(obj);
      if (!isFinite(_bb.max.x)) continue;
      obj.getWorldPosition(_wp);
      const ax = _bb.max.x;
      const ay = (_bb.min.y + _bb.max.y) * 0.5;
      const v = new THREE.Vector3(ax, ay, _wp.z).project(camera);
      layerScreen[k] = {
        x: (v.x * 0.5 + 0.5) * window.innerWidth,
        y: (-v.y * 0.5 + 0.5) * window.innerHeight,
        visible: (obj.userData._opacity ?? 1) > 0.05,
      };
    }
    window.dispatchEvent(new CustomEvent('kb-layer-pos', { detail: layerScreen }));
  }

  renderer.render(scene, camera);
}

window.MERIDIAN = {
  applyScroll, applyVariant, applyPalette,
  setLightIntensity(v) { state.lightIntensity = v; applyVariant(state.variant); },
  setEnvIntensity(v) { state.envIntensity = v; applyVariant(state.variant); },
  setBloom() {},
  setExplodeAmount(v) { state.explodeAmount = v; },
  setDetailAngle(chapterKey, presetName) {
    if (!ANGLE_PRESETS[presetName]) return;
    detailChapterAngles[chapterKey] = presetName;
    rebuildChapters();
  },
  ANGLE_PRESETS,
  loadLayerGLB,
  loadAllLayerGLBs,
  state,
  LAYERS, HOVER_PARTS,
};

applyVariant('paper');
applyPalette('graphite');
animate();
